import React, { useState, Fragment } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import {
  ClockIcon,
  XMarkIcon,
  BellSlashIcon,
  CheckCircleIcon,
  TableCellsIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  LinkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import snoozeService from '../../services/snoozeService';

const QUICK_DURATIONS = [
  { key: '1h', label: '1 hour', description: 'Short break' },
  { key: '4h', label: '4 hours', description: 'Half day' },
  { key: '24h', label: '24 hours', description: 'Full day' },
  { key: '1w', label: '1 week', description: 'Extended pause' },
];

// `icon` holds the Heroicon component itself (not an instance) so it
// can be rendered with `<typeInfo.icon className="..." />`. Storing the
// component reference lets the consumer size + color it inline rather
// than pre-baking those styles into the config.
const SNOOZE_TYPES = {
  table: { label: 'Table', icon: TableCellsIcon },
  metric: { label: 'Metric', icon: ChartBarIcon },
  validation_rule: { label: 'Validation Rule', icon: CheckBadgeIcon },
  connection: { label: 'Connection', icon: LinkIcon },
  pattern: { label: 'Pattern', icon: MagnifyingGlassIcon },
};

const SnoozeModal = ({
  isOpen,
  onClose,
  target,  // { type: 'table'|'metric'|etc, id: string, name: string, connectionId?: string }
  onSnoozeCreated
}) => {
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [customMinutes, setCustomMinutes] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleQuickSnooze = async (durationKey) => {
    setSelectedDuration(durationKey);
    setError(null);
    setIsSubmitting(true);

    try {
      await snoozeService.quickSnooze({
        snooze_type: target.type,
        target_id: target.id,
        target_name: target.name,
        duration_key: durationKey,
        connection_id: target.connectionId
      });

      setSuccess(true);
      setTimeout(() => {
        onSnoozeCreated?.();
        onClose();
        // Reset state
        setSuccess(false);
        setSelectedDuration(null);
        setReason('');
      }, 1000);
    } catch (err) {
      setError(err.message || 'Failed to create snooze');
      setSelectedDuration(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCustomSnooze = async () => {
    const minutes = parseInt(customMinutes, 10);
    if (!minutes || minutes < 1) {
      setError('Please enter a valid number of minutes');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await snoozeService.createSnooze({
        snooze_type: target.type,
        target_id: target.id,
        target_name: target.name,
        duration_minutes: minutes,
        reason: reason || undefined,
        connection_id: target.connectionId
      });

      setSuccess(true);
      setTimeout(() => {
        onSnoozeCreated?.();
        onClose();
        // Reset state
        setSuccess(false);
        setCustomMinutes('');
        setReason('');
      }, 1000);
    } catch (err) {
      setError(err.message || 'Failed to create snooze');
    } finally {
      setIsSubmitting(false);
    }
  };

  const typeInfo = SNOOZE_TYPES[target?.type] || { label: 'Alert', icon: BellSlashIcon };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {success ? (
                  <div className="text-center py-8">
                    <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">Snoozed!</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Alerts for this {typeInfo.label.toLowerCase()} will be paused
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900 flex items-center">
                        <BellSlashIcon className="h-6 w-6 text-gray-400 mr-2" />
                        Snooze Alerts
                      </DialogTitle>
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                        onClick={onClose}
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <typeInfo.icon className="h-6 w-6 text-gray-500 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{target?.name || target?.id}</p>
                          <p className="text-xs text-gray-500">{typeInfo.label}</p>
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}

                    <div className="mt-6">
                      <p className="text-sm font-medium text-gray-700 mb-3">Quick snooze</p>
                      <div className="grid grid-cols-2 gap-3">
                        {QUICK_DURATIONS.map((duration) => (
                          <button
                            key={duration.key}
                            onClick={() => handleQuickSnooze(duration.key)}
                            disabled={isSubmitting}
                            className={`flex flex-col items-center p-3 border rounded-lg transition-all ${
                              selectedDuration === duration.key
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <ClockIcon className="h-5 w-5 text-gray-400 mb-1" />
                            <span className="text-sm font-medium text-gray-900">{duration.label}</span>
                            <span className="text-xs text-gray-500">{duration.description}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-6">
                      <p className="text-sm font-medium text-gray-700 mb-3">Custom duration</p>
                      <div className="flex items-center space-x-3">
                        <input
                          type="number"
                          min="1"
                          value={customMinutes}
                          onChange={(e) => setCustomMinutes(e.target.value)}
                          placeholder="Minutes"
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                        <button
                          onClick={handleCustomSnooze}
                          disabled={isSubmitting || !customMinutes}
                          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Snooze
                        </button>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason (optional)
                      </label>
                      <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g., Known dbt migration issue"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SnoozeModal;
